import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getFireLog, saveFireLog, GetFireLogOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Flame, Plus, CheckCircle2, AlertTriangle, Clock, Trash2, Settings, Loader2, Info } from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

type FireLogData = GetFireLogOutputType;
type LogEntry = FireLogData['logs'][0];

const CHECK_TYPES = [
  { value: 'Call Point Check', label: '📍 Call Point Check', freq: 'Weekly' },
  { value: 'Sounder Check', label: '🔊 Sounder Check', freq: 'Monthly' },
  { value: 'Extinguisher Check', label: '🧯 Extinguisher Check', freq: 'Monthly' },
  { value: 'Evacuation Drill', label: '🚨 Evacuation Drill', freq: 'Yearly' },
];

function StatusCard({ label, overdue, lastDate, freq, note }: { label: string; overdue: boolean; lastDate?: string; freq: string; note?: string }) {
  const daysAgo = lastDate ? Math.floor((Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24)) : null;
  return (
    <div className={`rounded-lg border p-4 ${overdue ? 'border-destructive/50 bg-destructive/5' : 'border-border bg-card'}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{freq}</p>
          {note && <p className="text-xs text-primary font-medium mt-1">{note}</p>}
        </div>
        {overdue ? (
          <Badge variant="destructive" className="shrink-0 text-xs">OVERDUE</Badge>
        ) : (
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
        )}
      </div>
      {lastDate ? (
        <p className="text-xs text-muted-foreground mt-2">
          Last checked {daysAgo === 0 ? 'today' : `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`} · {format(parseISO(lastDate), 'd MMM yyyy')}
        </p>
      ) : (
        <p className="text-xs text-destructive font-medium mt-2">Never checked — log first check now</p>
      )}
    </div>
  );
}

export default function FireLog() {
  const { user } = useAuth();
  const [data, setData] = useState<FireLogData | null>(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const [form, setForm] = useState({ checkType: 'Call Point Check', location: '', result: 'Pass', notes: '', actionNeeded: '', checkDate: new Date().toISOString().slice(0, 16) });
  const [cpList, setCpList] = useState<string[]>([]);
  const [newCp, setNewCp] = useState('');

  const isManager = user?.role === 'Manager' || user?.role === 'Admin';

  const load = () => {
    getFireLog({}).then(d => { setData(d); setCpList(d.callPoints); }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); }, [user]);

  const handleAdd = async () => {
    if (!form.checkType) { toast.error('Select check type'); return; }
    if (form.checkType === 'Call Point Check' && !form.location) { toast.error('Enter the call point location'); return; }
    setSaving(true);
    try {
      await saveFireLog({ ...form, checkDate: new Date(form.checkDate).toISOString() });
      toast.success('Check logged');
      setAddOpen(false);
      setForm({ checkType: 'Call Point Check', location: '', result: 'Pass', notes: '', actionNeeded: '', checkDate: new Date().toISOString().slice(0, 16) });
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await saveFireLog({ deleteId: id }); toast.success('Entry deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleSaveCp = async () => {
    setSaving(true);
    try { await saveFireLog({ callPoints: cpList }); toast.success('Call points saved'); setConfigOpen(false); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const filteredLogs = (data?.logs || []).filter(l => filter === 'all' || l.checkType === filter);

  if (!isManager) return (
    <div className="px-4 md:px-8 py-12 text-center text-muted-foreground">
      <Flame className="w-10 h-10 mx-auto mb-3 text-red-400" />
      <p>Fire log is available to managers only.</p>
    </div>
  );

  if (loading) return <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;

  const overdue = data?.overdue;
  const overdueCount = Object.values(overdue || {}).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2"><Flame className="w-5 h-5 text-red-500" /> Fire Safety Log Book</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Weekly call points · Monthly sounder/extinguisher · Annual evacuation</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setConfigOpen(true)}><Settings className="w-3.5 h-3.5" /> Call Points</Button>
            <Button size="sm" className="gap-1.5 bg-primary text-primary-foreground" onClick={() => setAddOpen(true)}><Plus className="w-3.5 h-3.5" /> Log Check</Button>
          </div>
        </div>

        {/* Overdue banner */}
        {overdueCount > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
            <div>
              <p className="text-sm font-semibold text-destructive">{overdueCount} check{overdueCount > 1 ? 's are' : ' is'} overdue</p>
              <p className="text-xs text-muted-foreground">Log the checks below to keep your fire safety record up to date.</p>
            </div>
          </div>
        )}

        {/* Status cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatusCard label="Call Point Check" overdue={overdue?.callPoint ?? true} lastDate={data?.lastChecked.callPoint?.date} freq="Every 7 days" note={data?.suggestedCallPoint ? `Due: ${data.suggestedCallPoint}` : undefined} />
          <StatusCard label="Sounder Check" overdue={overdue?.sounder ?? true} lastDate={data?.lastChecked.sounder?.date} freq="Monthly" />
          <StatusCard label="Extinguisher Check" overdue={overdue?.extinguisher ?? true} lastDate={data?.lastChecked.extinguisher?.date} freq="Monthly" />
          <StatusCard label="Evacuation Drill" overdue={overdue?.evacuation ?? true} lastDate={data?.lastChecked.evacuation?.date} freq="Annually" />
        </div>

        {/* Call point suggestion */}
        {data?.suggestedCallPoint && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Info className="w-4 h-4 text-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">This Week's Call Point: <span className="text-primary">{data.suggestedCallPoint}</span></p>
                <p className="text-xs text-muted-foreground">Based on rotation schedule — the least recently tested call point.</p>
              </div>
            </div>
            <Button size="sm" onClick={() => { setForm(f => ({ ...f, checkType: 'Call Point Check', location: data.suggestedCallPoint! })); setAddOpen(true); }}>
              Log Now
            </Button>
          </div>
        )}

        {/* Log history */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Clock className="w-4 h-4" /> Check History ({filteredLogs.length})</h2>
            <div className="flex gap-1.5 flex-wrap">
              {['all', ...CHECK_TYPES.map(t => t.value)].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === f ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                  {f === 'all' ? 'All' : f.replace(' Check', '')}
                </button>
              ))}
            </div>
          </div>

          {filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Flame className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No checks logged yet. Click "Log Check" to add the first entry.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredLogs.map(log => (
                <div key={log.id} className="flex items-start gap-3 px-4 py-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${log.result === 'Pass' ? 'bg-green-500' : log.result === 'Fail' ? 'bg-destructive' : 'bg-orange-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{log.checkType}</span>
                      {log.location && <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">{log.location}</span>}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${log.result === 'Pass' ? 'bg-green-500/10 text-green-600' : log.result === 'Fail' ? 'bg-destructive/10 text-destructive' : 'bg-orange-500/10 text-orange-600'}`}>{log.result}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {log.checkDate ? format(parseISO(log.checkDate), 'd MMM yyyy HH:mm') : 'Date unknown'} · {log.checkedByName || 'Unknown'}
                    </p>
                    {log.notes && <p className="text-xs text-foreground mt-1">{log.notes}</p>}
                    {log.actionNeeded && <p className="text-xs text-orange-600 font-medium mt-0.5">⚠ Action: {log.actionNeeded}</p>}
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete this log entry?</AlertDialogTitle></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(log.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add log dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Flame className="w-4 h-4 text-red-500" /> Log Fire Check</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Check Type *</label>
              <Select value={form.checkType} onValueChange={v => setForm(f => ({ ...f, checkType: v, location: '' }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CHECK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.checkType === 'Call Point Check' && (
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Call Point Location *</label>
                <Select value={form.location} onValueChange={v => setForm(f => ({ ...f, location: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select call point…" /></SelectTrigger>
                  <SelectContent>{(data?.callPoints || []).map(cp => <SelectItem key={cp} value={cp}>{cp}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Date & Time</label>
                <Input type="datetime-local" value={form.checkDate} onChange={e => setForm(f => ({ ...f, checkDate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Result</label>
                <Select value={form.result} onValueChange={v => setForm(f => ({ ...f, result: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pass">✅ Pass</SelectItem>
                    <SelectItem value="Fail">❌ Fail</SelectItem>
                    <SelectItem value="Action Required">⚠️ Action Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Notes</label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Any observations…" />
            </div>
            {form.result !== 'Pass' && (
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Action Required</label>
                <Textarea value={form.actionNeeded} onChange={e => setForm(f => ({ ...f, actionNeeded: e.target.value }))} rows={2} placeholder="Describe what action is needed…" />
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleAdd} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Saving…</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-2" />Log Check</>}
              </Button>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Call points config dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Manage Call Points</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground">Add all fire call points in the building. The system will rotate through them weekly.</p>
            <div className="space-y-2">
              {cpList.map((cp, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={cp} onChange={e => setCpList(prev => prev.map((x, j) => j === i ? e.target.value : x))} className="flex-1 text-sm h-8" />
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => setCpList(prev => prev.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newCp} onChange={e => setNewCp(e.target.value)} placeholder="e.g. CP6 - Cellar" className="flex-1 text-sm h-8" onKeyDown={e => { if (e.key === 'Enter' && newCp.trim()) { setCpList(prev => [...prev, newCp.trim()]); setNewCp(''); } }} />
              <Button size="sm" className="h-8" onClick={() => { if (newCp.trim()) { setCpList(prev => [...prev, newCp.trim()]); setNewCp(''); } }}>Add</Button>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={handleSaveCp} disabled={saving} className="flex-1">{saving ? 'Saving…' : 'Save Call Points'}</Button>
              <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
